/**
 * Session recording — the recorder half.
 *
 * rrweb writes down the DOM and every change to it as plain JSON; this module
 * buffers those events and POSTs them in chunks to one endpoint. A player
 * rebuilds the page from them later.
 *
 * The portal records only. Watching a recording is a super-admin action in the
 * subscription platform, and the read endpoints reject a portal token — so
 * there is deliberately no list screen or player here.
 *
 * Section numbers in the comments below point at the backend team's
 * "Session Recording — Frontend Guide".
 */
import {getTenantDomain} from 'common/HelperFunctions'
import {SECRET_KEY, base_url} from 'common/constants'
import {type BufferEntry, FULL_SNAPSHOT, takeBatchFrom} from 'common/sessionRecording/chunking'
import {inlineFontsInSnapshot} from 'common/sessionRecording/fontInlining'
import {record} from 'rrweb'
import store from 'setup/redux/Store'

const ENDPOINT = `${base_url}session-recordings/events`

/** Kept in step with the pinned `rrweb` dependency — replay bugs get diagnosed from it. */
const RRWEB_VERSION = '2.1.1'

// Flush triggers (§7.2). Whichever trips first wins.
const FLUSH_MS = 10_000
const FLUSH_EVENTS = 200
const FLUSH_BYTES = 512 * 1024

/** Starting ceiling for one request; the server refuses above 2 MB (§7.2.1). */
const INITIAL_MAX_CHUNK_BYTES = 1_500_000

/** Floor for the halving in §8.1 — below this the recording is not worth keeping alive. */
const MIN_MAX_CHUNK_BYTES = 64 * 1024

/** `keepalive` is capped at 64 KB by the Fetch spec; over it the request is dropped silently (§7.6). */
const KEEPALIVE_MAX_BYTES = 60 * 1024

let buffer: BufferEntry[] = []
let bufferBytes = 0
let maxChunkBytes = INITIAL_MAX_CHUNK_BYTES
let seq = 0
let sessionId: string | null = null
let startedAt: number | null = null
let stopFn: (() => void) | null = null
let failures = 0
let timer: ReturnType<typeof setInterval> | null = null
let onVisibilityChange: (() => void) | null = null

/**
 * One chunk in the air at a time.
 *
 * Three things call `flush`: the 10s interval, the size/count triggers in
 * `emit`, and the tab-hidden handler. Without this guard two of them can
 * overlap, and since a retry rolls `seq` back with `seq--` while the other call
 * has already taken the next number, the rollback lands on the wrong chunk and
 * the replay misorders. Skipping is safe — the interval picks the buffer up
 * ten seconds later.
 */
let inFlight = false

/** Non-fatal diagnostics. Never throws, never touches the DOM (see `emit` below). */
const report = (message: string, detail?: unknown) => {
  if (import.meta.env.DEV) console.warn(`[session-recording] ${message}`, detail ?? '')
}

const apiHeaders = (): Record<string, string> => {
  const {accessToken} = store.getState().userReducer
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    domain: getTenantDomain(),
    platform: 'web',
    platformKey: SECRET_KEY.web,
    version: '1.0.0',
  }
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`
  return headers
}

const buildMeta = () => {
  const {user, userType, impersonation} = store.getState().userReducer
  return {
    // Deliberately no userId / tenant — the backend takes both from the token
    // and overwrites anything sent here (§7.5).
    username: user?.username ?? user?.email ?? user?.name ?? '',
    userType,
    impersonated: Boolean(impersonation),
    appVersion: String(import.meta.env.VITE_BUILD_VERSION ?? ''),
    buildEnv: String(import.meta.env.VITE_APP_BUILD_NAME ?? import.meta.env.MODE),
    origin: window.location.origin,
    rrwebVersion: RRWEB_VERSION,
    userAgent: navigator.userAgent,
    screen: {width: window.screen.width, height: window.screen.height},
  }
}

const clearTimers = () => {
  if (timer) clearInterval(timer)
  timer = null
  if (onVisibilityChange) document.removeEventListener('visibilitychange', onVisibilityChange)
  onVisibilityChange = null
}

/** Stop for good. Nothing restarts until the next login or a page reload (§8.2). */
const disable = () => {
  if (stopFn) stopFn()
  stopFn = null
  clearTimers()
  buffer = []
  bufferBytes = 0
}

/**
 * The session outlived a server limit (§8.3). Recording continues under a new
 * id; the old recording stays intact up to the point it was cut off.
 */
const renewSession = () => {
  sessionId = crypto.randomUUID()
  startedAt = Date.now()
  seq = 0
  buffer = []
  bufferBytes = 0
  // Without this the new session has no snapshot to replay from until the next
  // 5-minute checkout, so its first minutes are unplayable.
  record.takeFullSnapshot()
}

/** `takeBatchFrom` bound to the live buffer, keeping the running byte total in step. */
const takeBatch = (budget: number, allowDrop: boolean): BufferEntry[] => {
  const {batch, removedBytes} = takeBatchFrom(buffer, budget, allowDrop)
  bufferBytes -= removedBytes
  return batch
}

/**
 * The wire limit: the server rejects a request whose Content-Length exceeds
 * 2 MB, measured on the body as sent. Chunks are gzipped before sending, so
 * this is checked against the COMPRESSED size — raw size is almost irrelevant
 * (rrweb JSON compresses roughly 10x). Kept under the true limit to leave room
 * for proxy overhead.
 */
const WIRE_MAX_BYTES = 1_900_000

/**
 * Gzip a JSON string for the request body. Returns null where the browser has
 * no CompressionStream (Safari < 16.4) — the caller then sends the raw body,
 * accepting raw-size limits.
 *
 * Verified against the live backend: its body parser inflates
 * `Content-Encoding: gzip` transparently (a valid gzipped probe reached auth;
 * a corrupt one was rejected at the parse step with 400), and the 2 MB cap is
 * enforced on Content-Length — the compressed size. This is what lets full
 * snapshots carry the page's CSS inline and still fit.
 */
const gzipBody = async (raw: string): Promise<ArrayBuffer | null> => {
  if (typeof CompressionStream === 'undefined') return null
  try {
    return await new Response(
      new Blob([raw]).stream().pipeThrough(new CompressionStream('gzip'))
    ).arrayBuffer()
  } catch {
    return null
  }
}

const flush = async ({keepalive = false} = {}): Promise<void> => {
  if (!buffer.length || !stopFn) return
  if (inFlight) return

  // Take the events out of the buffer BEFORE awaiting, so events arriving
  // during the request are neither lost nor sent twice.
  const budget = keepalive ? Math.min(maxChunkBytes, KEEPALIVE_MAX_BYTES) : maxChunkBytes
  const batch = takeBatch(budget, !keepalive)
  if (!batch.length) return

  // Locked from the moment events leave the buffer. The font-inlining await
  // below yields to the event loop, and an emit-triggered flush entering
  // during that window would interleave takeBatch/putBack on the shared
  // buffer and corrupt seq — the server then drops a later chunk as a
  // "duplicate", which replays as a frozen page with the timeline running.
  inFlight = true

  // Increment when the chunk is CREATED, not when it succeeds — otherwise a
  // retry after a failure reuses a number and the replay misorders (§7.4).
  const mySeq = seq++

  /**
   * Undo the take, in order, so a retry sends the same chunk under the same
   * seq — which is what makes the server's duplicate handling work.
   */
  const putBack = () => {
    buffer.unshift(...batch)
    bufferBytes += batch.reduce((total, entry) => total + entry.bytes, 0)
    seq--
  }

  try {
    const events = batch.map((entry) => entry.event)

    // Icon glyphs are font files referenced by URL inside the captured CSS —
    // unreachable from whoever replays a localhost recording, so embed them
    // (loaded families only, byte-budgeted; no-op in production, where font
    // URLs are publicly reachable). Must happen before the payload is
    // serialized, and failure must never cost the chunk.
    for (const event of events) {
      if (event.type === FULL_SNAPSHOT) {
        try {
          await inlineFontsInSnapshot(event)
        } catch {
          // fonts missing beats a chunk missing
        }
      }
    }

    // A session whose first chunk carries no type 2 event replays as a blank
    // white screen — the player is applying edits to a page it never received.
    // Nothing downstream reports it: the chunks upload, the 200s come back, and
    // the recording looks healthy right up until someone tries to watch it
    // (§12.1). This is the only moment it is cheaply detectable.
    if (mySeq === 0 && !events.some((event) => event.type === FULL_SNAPSHOT)) {
      report('first chunk has no full snapshot — this session will replay blank')
    }

    const payload = {
      sessionId,
      seq: mySeq,
      startedAt,
      capturedAt: Date.now(),
      events,
      meta: buildMeta(),
    }

    const raw = JSON.stringify(payload)
    const compressed = await gzipBody(raw)
    const wireBytes = compressed ? compressed.byteLength : raw.length

    // The wire limit is enforced BEFORE sending, on what will actually travel.
    // Over it: a multi-event batch goes back and is retried in smaller pieces;
    // a single event that cannot fit any request is dropped — loudly when it is
    // the snapshot, because that costs the whole session (§12.1).
    if (wireBytes > (keepalive ? KEEPALIVE_MAX_BYTES : WIRE_MAX_BYTES)) {
      if (keepalive || batch.length > 1) {
        // Tab-hidden: leave it for the next full-budget flush. Ordinary flush:
        // halve the raw budget so the retry goes out in smaller pieces.
        putBack()
        if (!keepalive) maxChunkBytes = Math.max(MIN_MAX_CHUNK_BYTES, Math.floor(maxChunkBytes / 2))
        return
      }
      seq-- // the dropped chunk never travels; free its number
      if (events[0]?.type === FULL_SNAPSHOT) {
        console.warn(
          `[session-recording] a full snapshot compressed to ${wireBytes} bytes, ` +
            'over the 2 MB wire limit — dropped; this session will replay blank.'
        )
      } else {
        report(`dropped a chunk of ${wireBytes} wire bytes — over the limit`)
      }
      return
    }

    const headers = apiHeaders()
    if (compressed) headers['Content-Encoding'] = 'gzip'

    // Plain fetch rather than the axios wrapper on purpose: the response
    // interceptor in AuthHelpers raises the "server unreachable" banner on a
    // failed call, and a dropped recording chunk must stay invisible to the user.
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      keepalive,
      headers,
      body: compressed ?? raw,
    })

    // These never fix themselves by retrying. 503 in particular is the normal
    // answer when the feature is simply switched off (§8.2).
    if ([401, 403, 404, 503].includes(res.status)) {
      report(`disabling after ${res.status}`)
      disable()
      return
    }

    if (res.status === 429) {
      // Slow down, not an error. The chunk goes back so nothing is lost.
      putBack()
      const wait = Number(res.headers.get('Retry-After') || 60) * 1000
      setTimeout(() => void flush(), wait)
      return
    }

    if (res.status === 413 || res.status === 422) {
      const {message} = await res.json().catch(() => ({message: ''}))
      const text = String(message ?? '')

      // One variant of each is permanent for this session — every later chunk
      // would get the same answer forever, so branch on the message, not the
      // status code alone (§8.3).
      if (/Session size limit|Invalid startedAt/i.test(text)) {
        report(`renewing session after ${res.status}: ${text}`)
        renewSession()
        return
      }

      // "Chunk too large" usually means our byte estimate was optimistic:
      // halve the budget and let the retry go out in smaller pieces rather
      // than losing ten seconds of recording. But a single-event chunk cannot
      // be made smaller by halving anything — putting it back would retry the
      // identical request forever, wedging the whole session behind one
      // unsendable event. Drop it instead, loudly when it is the snapshot.
      if (/Chunk too large/i.test(text)) {
        if (batch.length === 1) {
          seq-- // the server stored nothing; free the number
          if (events[0]?.type === FULL_SNAPSHOT) {
            console.warn(
              '[session-recording] server refused the full snapshot as too large ' +
                'even gzipped — dropped; this session will replay blank. If this ' +
                'appears, the server is measuring the INFLATED size, not ' +
                'Content-Length, and that needs a backend-side look.'
            )
          } else {
            report('server refused a single-event chunk as too large — dropped')
          }
          return
        }
        maxChunkBytes = Math.max(MIN_MAX_CHUNK_BYTES, Math.floor(maxChunkBytes / 2))
        putBack()
        return
      }

      // Otherwise one malformed chunk. It is out of the buffer already.
      report(`dropped a chunk: ${res.status} ${text}`)
      return
    }

    if (res.status >= 500) {
      putBack()
      if (++failures >= 5) disable()
      return
    }

    // Anything left is a 200 — including `duplicate: true`, which means the
    // chunk was already stored and is not an error. Consecutive, not
    // cumulative: a success resets the count (§8.2).
    failures = 0
  } catch {
    putBack()
    if (++failures >= 5) disable()
  } finally {
    inFlight = false
  }
}

/**
 * Start recording. Call after login succeeds, and on app boot when a token is
 * already present — never on the login page, which has a password field and no
 * token to send with (§4).
 */
export const startRecording = () => {
  if (stopFn) return
  if (!store.getState().userReducer.accessToken) return

  // ORDER MATTERS: every one of these resets must happen BEFORE `record()`.
  // rrweb emits the full snapshot synchronously during that call, so anything
  // cleared afterwards throws the snapshot away and the session replays as a
  // blank screen (§12.1). The buffer also lives at module scope rather than in
  // a component or a ref for the same reason — a remount must not be able to
  // replace the array `emit` is pushing into.
  sessionId = crypto.randomUUID()
  startedAt = Date.now()
  seq = 0
  failures = 0
  maxChunkBytes = INITIAL_MAX_CHUNK_BYTES
  buffer = []
  bufferBytes = 0
  inFlight = false

  // `record` returns undefined if rrweb refuses to start (a document it cannot
  // observe). Nothing to tear down in that case, and `stopFn` staying null
  // keeps `flush` inert.
  stopFn =
    record({
      /**
       * Push and nothing else. rrweb is watching the document, so a DOM write in
       * here is a mutation that rrweb records, which fires emit again, at frame
       * rate until the tab runs out of memory — with no console error and no
       * failed request to point at (§6).
       *
       * Each event is measured once, on the way in; stringifying the whole buffer
       * per event instead is O(n²) on a busy page.
       */
      emit(event) {
        const bytes = JSON.stringify(event).length + 1
        buffer.push({event, bytes})
        bufferBytes += bytes

        if (buffer.length >= FLUSH_EVENTS || bufferBytes >= FLUSH_BYTES) void flush()
      },

      // Re-emit a full snapshot every 5 minutes. Without it a long session can
      // only be replayed from the very beginning.
      checkoutEveryNms: 5 * 60 * 1000,

      // Unsampled mousemove alone is most of the payload.
      sampling: {mousemove: 50, scroll: 150, input: 'last'},

      // The difference between recording that a user typed in the password field
      // and recording the password.
      maskAllInputs: true,

      // Nothing here is needed to replay a portal session, and each is
      // expensive in snapshot bytes.
      collectFonts: false,
      recordCanvas: false,
      // Images are referenced by URL, not embedded. In production that is
      // right: tenant portals serve images from publicly reachable URLs, and
      // inlining would be a real hazard — a page of student photos as base64
      // barely compresses (JPEG is already compressed), and could push a
      // snapshot past the wire limit, trading a missing avatar for a blank
      // replay. On localhost the viewer's machine cannot reach this machine's
      // /media/..., so local recordings embed images instead — the pages being
      // recorded locally are light, and the wire-size check still guards the
      // limit.
      inlineImages: window.location.hostname === 'localhost',

      // Drops comments, <meta> and other nodes that never render.
      slimDOMOptions: 'all',

      // CSS travels INSIDE the recording, so replay renders styled no matter
      // which machine watches it — no dependency on the portal being reachable
      // at watch time. Inlining is what makes the snapshot big: measured at
      // 2,631,580 bytes on this app's production build, and ~2.8 MB against
      // the dev server (whose CSS lives in <style> tags captured as DOM text
      // regardless of this option). That is only affordable because chunks are
      // gzipped on the wire — rrweb JSON compresses ~10x, landing well under
      // the server's 2 MB Content-Length cap. Browsers without
      // CompressionStream (Safari < 16.4) cannot compress, so they record
      // without inlined CSS instead: an unstyled replay, never a dropped
      // snapshot and never a blank one.
      inlineStylesheet: typeof CompressionStream !== 'undefined',
    }) ?? null

  if (!stopFn) {
    report('rrweb declined to start')
    return
  }

  timer = setInterval(() => void flush(), FLUSH_MS)

  // visibilitychange, not beforeunload — mobile Safari does not reliably fire
  // beforeunload. Losing the last few seconds is acceptable; losing them
  // silently to the 64 KB keepalive cap is not, hence the smaller budget (§7.6).
  onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') void flush({keepalive: true})
  }
  document.addEventListener('visibilitychange', onVisibilityChange)
}

/**
 * Stop recording. A recording belongs to one user, so logout must stop it — the
 * next login generates a fresh `sessionId` rather than continuing this one (§4).
 */
export const stopRecording = () => disable()

/** True while rrweb is attached. Exported for tests. */
export const isRecording = () => stopFn !== null
